import React, { useState, useEffect, useCallback, useRef,useMemo  } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import {
  hasSalesQuotePdf,
  resolveSqPdfDownloadUrl
} from '../utils/sqPdfDownload'

const API_URL = process.env.REACT_APP_API_URL

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function parseAdditionalCcEmails(value) {
  return String(value || '')
    .split(/[,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
}

function isValidEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
}

function buildCcEmails(userEmail, additionalInput) {
  const result = []
  const seen = new Set()
  const addEmail = (email) => {
    const trimmed = String(email || '').trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    result.push(trimmed)
  }

  addEmail(userEmail)
  parseAdditionalCcEmails(additionalInput).forEach(addEmail)
  return result
}

function flattenCustomersToContactRows(customers) {
  const rows = []
  if (!Array.isArray(customers)) return rows

  customers.forEach((customer) => {
    const customerName = String(customer.customerName || '').trim()
    const contacts = Array.isArray(customer.contacts) ? customer.contacts : []

    if (contacts.length === 0) {
      const email = String(customer.email || '').trim()
      if (email) {
        rows.push({
          customerId: customer._id || customer.id || '',
          contactId: '',
          customerName,
          contactPerson: '',
          designation: '',
          email,
          contactNumber: String(customer.contactNumber || customer.phonenumber || '').trim()
        })
      }
      return
    }

    contacts.forEach((contact) => {
      const email = String(contact.email || '').trim()
      if (!email) return
      const status = String(contact.status || 'active').toLowerCase()
      if (status === 'inactive') return

      rows.push({
        customerId: customer._id || customer.id || '',
        contactId: contact._id || contact.id || '',
        customerName,
        contactPerson: String(contact.contactPerson || '').trim(),
        designation: String(contact.designation || '').trim(),
        email,
        contactNumber: String(contact.contactNumber || contact.phoneNumber || '').trim()
      })
    })
  })

  return rows
}

function getMaterialGstPercent(row) {
  if (row?.gstPercent != null && row.gstPercent !== '') return row.gstPercent
  if (row?.gst != null && row.gst !== '') return row.gst
  return null
}

function formatMaterialMoney(value) {
  if (value == null || value === '') return '-'
  const n = parseFloat(value)
  return Number.isFinite(n) ? n.toFixed(2) : String(value)
}

function getMaterialGstValueDisplay(row) {
  if (row?.gstValue != null && row.gstValue !== '') {
    return formatMaterialMoney(row.gstValue)
  }
  const basic = parseFloat(row?.basicPrice) || 0
  const qty = parseFloat(row?.quantity) || 0
  const gst = parseFloat(getMaterialGstPercent(row)) || 0
  if (basic > 0 && qty > 0 && gst > 0) {
    return ((basic * qty) * (gst / 100)).toFixed(2)
  }
  return '-'
}

function getMaterialTotalValueDisplay(row) {
  if (row?.totalValue != null && row.totalValue !== '') {
    return formatMaterialMoney(row.totalValue)
  }
  const basic = parseFloat(row?.basicPrice) || 0
  const qty = parseFloat(row?.quantity) || 0
  const gst = parseFloat(getMaterialGstPercent(row)) || 0
  if (basic > 0 && qty > 0) {
    const totalBasic = basic * qty
    const gstAmount = totalBasic * (gst / 100)
    return (totalBasic + gstAmount).toFixed(2)
  }
  return '-'
}

function calcGrossTotalsFromMaterials(materialRows) {
  return materialRows.reduce(
    (acc, row) => {
      const lineBasic = (parseFloat(row?.basicPrice) || 0) * (parseFloat(row?.quantity) || 0)
      let gstValue = parseFloat(row?.gstValue)
      if (!Number.isFinite(gstValue)) {
        const gst = parseFloat(getMaterialGstPercent(row)) || 0
        gstValue = (lineBasic * gst) / 100
      }
      let totalValue = parseFloat(row?.totalValue)
      if (!Number.isFinite(totalValue)) {
        totalValue = lineBasic + gstValue
      }
      return {
        grossTotalBasicPrice: acc.grossTotalBasicPrice + lineBasic,
        grossGstValue: acc.grossGstValue + gstValue,
        grossTotalValue: acc.grossTotalValue + totalValue
      }
    },
    { grossTotalBasicPrice: 0, grossGstValue: 0, grossTotalValue: 0 }
  )
}

function getGrossTotalsFromSource(source) {
  const grossTotalBasicPrice = Number(source?.grossTotalBasicPrice)
  const grossGstValue = Number(source?.grossGstValue)
  const grossTotalValue = Number(source?.grossTotalValue)
  if (
    Number.isFinite(grossTotalBasicPrice) &&
    Number.isFinite(grossGstValue) &&
    Number.isFinite(grossTotalValue)
  ) {
    return { grossTotalBasicPrice, grossGstValue, grossTotalValue }
  }
  return null
}

function getQuoteGrossTotals(quote, materialRows) {
  const fromQuote = getGrossTotalsFromSource(quote)
  if (fromQuote) return fromQuote

  const customers = getQuoteCustomers(quote)
  for (const customer of customers) {
    const fromCustomer = getGrossTotalsFromSource(customer)
    if (fromCustomer) return fromCustomer
  }

  return calcGrossTotalsFromMaterials(materialRows)
}

function pickQuoteFromResponse(responseData, id) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (dataBlock == null) return null
  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') return null

  if (
    !Array.isArray(dataBlock) &&
    dataBlock.quotes == null &&
    (dataBlock._id != null || dataBlock.quoteNumber != null)
  ) {
    return dataBlock
  }

  const list = dataBlock.quotes
  if (!Array.isArray(list) || list.length === 0) return null
  const found = list.find((q) => String(q._id || q.id) === String(id))
  return found || list[0]
}

function formatDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, '/')
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/)
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]}/${dateTimeMatch[2]}/${dateTimeMatch[3]}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  return formatDateParts(d)
}

function formatDateParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date)

  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  return `${get('day')}/${get('month')}/${get('year')}`
}

function formatDateTime(value) {
  if (!value) return '-'
  const str = String(value).trim()
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})/)
  if (dateTimeMatch) {
    const [, dd, mm, yyyy, hh, min] = dateTimeMatch
    const hour = parseInt(hh, 10)
    const period = hour >= 12 ? 'PM' : 'AM'
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${dd}/${mm}/${yyyy}, ${String(h12).padStart(2, '0')}:${min} ${period}`
  }

  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).formatToParts(d)

  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  const dayPeriod = get('dayPeriod').toUpperCase()
  return `${get('day')}/${get('month')}/${get('year')}, ${get('hour')}:${get('minute')} ${dayPeriod}`
}

function displayUser(ref) {
  if (!ref) return '-'
  if (typeof ref === 'string') return ref
  if (ref.name) return ref.name
  if (ref.email) return ref.email
  return '-'
}

function capitalizeStatus(value) {
  if (!value) return '-'
  const s = String(value)
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function mapAttachmentFromApi(item) {
  return {
    s3Key: item?.s3Key || '',
    originalFileName: item?.originalFileName || '',
    contentType: item?.contentType || '',
    uploadedAt: item?.uploadedAt || '',
    url: item?.url || ''
  }
}

function getSqOwnerListFromQuote(quote) {
  return Array.isArray(quote?.quoteOwner) ? quote.quoteOwner : []
}


function getAttachmentLabel(att) {
  if (!att) return 'Attachment'
  if (att.originalFileName) return att.originalFileName
  const key = att.s3Key || ''
  if (!key) return 'Attachment'
  const parts = String(key).split('/')
  return parts[parts.length - 1] || key
}

function getFileExtension(fileName, contentType) {
  const name = String(fileName || '').trim()
  const dotExt = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
  if (dotExt && dotExt.length <= 6) return dotExt

  const typeMap = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/msword': 'doc',
    'text/plain': 'txt',
    'application/zip': 'zip'
  }
  return typeMap[String(contentType || '').toLowerCase()] || ''
}

function getFileTypeMeta(extension, contentType) {
  const ext = String(extension || getFileExtension('', contentType)).toLowerCase()

  if (ext === 'pdf') {
    return { icon: 'fa-file-pdf', color: '#dc3545', bg: '#fde8ea', label: 'PDF' }
  }
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
    return { icon: 'fa-file-excel', color: '#198754', bg: '#e8f5ee', label: ext.toUpperCase() || 'EXCEL' }
  }
  if (ext === 'docx' || ext === 'doc') {
    return { icon: 'fa-file-word', color: '#0d6efd', bg: '#e7f1ff', label: ext.toUpperCase() || 'WORD' }
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return { icon: 'fa-file-image', color: '#6f42c1', bg: '#f3ebff', label: ext.toUpperCase() || 'IMAGE' }
  }
  if (ext === 'zip' || ext === 'rar' || ext === '7z') {
    return { icon: 'fa-file-zipper', color: '#fd7e14', bg: '#fff3e6', label: ext.toUpperCase() || 'ZIP' }
  }
  if (ext === 'ppt' || ext === 'pptx') {
    return { icon: 'fa-file-powerpoint', color: '#d63384', bg: '#fce8f3', label: ext.toUpperCase() || 'PPT' }
  }
  if (ext === 'txt') {
    return { icon: 'fa-file-lines', color: '#6c757d', bg: '#f1f3f5', label: 'TXT' }
  }
  return {
    icon: 'fa-file',
    color: '#495057',
    bg: '#f8f9fa',
    label: ext ? ext.toUpperCase() : 'FILE'
  }
}

function getQuoteTermsRows(quote) {
  if (Array.isArray(quote?.terms) && quote.terms.length > 0) {
    return quote.terms.map((t) => ({
      quoteTerm: t.quoteTerm || t.rfqTerm || '-',
      quoteResponse: t.quoteResponse || t.termsResponse || '-'
    }))
  }
  return []
}

function getQuoteCustomers(quote) {
  if (Array.isArray(quote?.customer) && quote.customer.length > 0) {
    return quote.customer
  }
  if (Array.isArray(quote?.customers) && quote.customers.length > 0) {
    return quote.customers
  }
  return []
}

function applyResendEmailToQuote(prevQuote, responseData, customerEmail) {
  if (!prevQuote) return prevQuote

  const data = responseData?.data
  const emailLink =
    data?.result?.quoteLink ||
    data?.result?.emailLink ||
    data?.result?.customerLink ||
    ''
  const mailKey = customerEmail.trim().toLowerCase()

  const updateCustomerRow = (customer) => {
    if (String(customer.customerMailId || '').trim().toLowerCase() !== mailKey) {
      return customer
    }
    return {
      ...customer,
      ...(data?.customerName ? { customerName: data.customerName } : {}),
      ...(emailLink ? { emailLink } : {})
    }
  }

  const nextCustomer = Array.isArray(prevQuote.customer)
    ? prevQuote.customer.map(updateCustomerRow)
    : prevQuote.customer
  const nextCustomers = Array.isArray(prevQuote.customers)
    ? prevQuote.customers.map(updateCustomerRow)
    : prevQuote.customers

  return {
    ...prevQuote,
    isMailSent: true,
    ...(data?.pdfLink ? { pdfLink: data.pdfLink } : {}),
    ...(data?.pdfDownloadUrl ? { pdfDownloadUrl: data.pdfDownloadUrl } : {}),
    ...(Array.isArray(nextCustomer) ? { customer: nextCustomer } : {}),
    ...(Array.isArray(nextCustomers) ? { customers: nextCustomers } : {})
  }
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
)

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
)

const AttachmentTileGrid = ({ attachments, downloadingS3Key, onDownload, disabled }) => {
  if (!attachments?.length) return null

  return (
    <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
      {attachments.map((att) => {
        const fileName = getAttachmentLabel(att)
        const fileMeta = getFileTypeMeta(
          getFileExtension(fileName, att.contentType),
          att.contentType
        )
        const isDownloading = downloadingS3Key === att.s3Key
        return (
          <button
            key={att.s3Key}
            type="button"
            className="border-0 text-center"
            title={`Open ${fileName}`}
            onClick={() => onDownload(att.s3Key, att.url)}
            disabled={disabled || isDownloading}
            style={{
              width: '92px',
              padding: '10px 8px',
              borderRadius: '10px',
              backgroundColor: fileMeta.bg,
              border: `1px solid ${fileMeta.color}33`,
              cursor: disabled || isDownloading ? 'not-allowed' : 'pointer',
              opacity: isDownloading ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: fileMeta.color,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                marginBottom: '8px'
              }}
            >
              {isDownloading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <i className={`fa-solid ${fileMeta.icon}`} />
              )}
            </div>
            <div
              title={fileName}
              style={{
                fontSize: '11px',
                lineHeight: 1.3,
                wordBreak: 'break-word',
                color: '#212529',
                width: '100%'
              }}
            >
              {fileName.length > 22 ? `${fileName.slice(0, 19)}...` : fileName}
            </div>
          </button>
        )
      })}
    </div>
  )
}

const SalesQuotesView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [quote, setQuote] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [resendingFor, setResendingFor] = useState('')
  const [isOpeningPdf, setIsOpeningPdf] = useState(false)
  const [downloadingS3Key, setDownloadingS3Key] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [additionalCcEmails, setAdditionalCcEmails] = useState('')
  const [ccUserSuggestions, setCcUserSuggestions] = useState([])
  const [isSearchingCcUsers, setIsSearchingCcUsers] = useState(false)
  const ccSearchDebounceRef = useRef(null)
  const ccSearchRequestIdRef = useRef(0)

  
    const ownerAttachments = useMemo(() => {
    return getSqOwnerListFromQuote(quote)
      .map(mapAttachmentFromApi)
      .filter((att) => att.s3Key)
  }, [quote])
 

  useEffect(() => {
    return () => {
      if (ccSearchDebounceRef.current) {
        clearTimeout(ccSearchDebounceRef.current)
      }
    }
  }, [])

  // --- Additional CC email autocomplete (get-users + get-all-customers) ---
  const getCcSearchTerm = (value) => {
    const segments = String(value || '').split(/[,;]/)
    return segments[segments.length - 1].trim()
  }

  const searchCcUsers = async (term, requestId) => {
    try {
      // Search internal users and customer contacts in parallel so
      // customer emails also appear as CC suggestions
      const [usersResult, customersResult] = await Promise.allSettled([
        axios.post(API_URL, {
          action: 'get-users',
          page: 1,
          limit: 25,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          search: term
        }),
        axios.post(API_URL, {
          action: 'get-all-customers',
          page: 1,
          limit: 25,
          search: term,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        })
      ])
      if (requestId !== ccSearchRequestIdRef.current) return

      const suggestions = []
      const seen = new Set()
      const addSuggestion = (name, email, sourceLabel) => {
        const trimmed = String(email || '').trim()
        if (!isValidEmailAddress(trimmed)) return
        const key = trimmed.toLowerCase()
        if (seen.has(key)) return
        seen.add(key)
        suggestions.push({
          name: String(name || '').trim() || trimmed,
          email: trimmed,
          sourceLabel
        })
      }

      if (usersResult.status === 'fulfilled') {
        const responseData = parseApiResponse(usersResult.value.data)
        const users = Array.isArray(responseData?.users)
          ? responseData.users
          : Array.isArray(responseData?.data?.users)
            ? responseData.data.users
            : []
        users.forEach((user) => addSuggestion(user?.name, user?.email, 'User'))
      }

      if (customersResult.status === 'fulfilled') {
        const responseData = parseApiResponse(customersResult.value.data)
        const list =
          responseData?.data?.customers ||
          responseData?.customers ||
          (Array.isArray(responseData?.data) ? responseData.data : [])
        if (Array.isArray(list)) {
          const lower = term.toLowerCase()
          const contactRows = flattenCustomersToContactRows(list)
          const matching = contactRows.filter(
            (row) =>
              String(row.email || '').toLowerCase().includes(lower) ||
              String(row.contactPerson || '').toLowerCase().includes(lower) ||
              String(row.customerName || '').toLowerCase().includes(lower)
          )
          ;(matching.length > 0 ? matching : contactRows).forEach((row) =>
            addSuggestion(
              row.contactPerson || row.customerName,
              row.email,
              row.customerName || 'Customer'
            )
          )
        }
      }

      setCcUserSuggestions(suggestions)
    } catch (error) {
      console.error('Search CC users error:', error)
      if (requestId === ccSearchRequestIdRef.current) {
        setCcUserSuggestions([])
      }
    } finally {
      if (requestId === ccSearchRequestIdRef.current) {
        setIsSearchingCcUsers(false)
      }
    }
  }

  const handleAdditionalCcChange = (e) => {
    const value = e.target.value
    setAdditionalCcEmails(value)

    ccSearchRequestIdRef.current += 1
    const requestId = ccSearchRequestIdRef.current
    if (ccSearchDebounceRef.current) {
      clearTimeout(ccSearchDebounceRef.current)
    }

    const term = getCcSearchTerm(value)
    if (term.length < 3) {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
      return
    }

    setIsSearchingCcUsers(true)
    ccSearchDebounceRef.current = setTimeout(() => {
      searchCcUsers(term, requestId)
    }, 400)
  }

  const handleSelectCcUser = (email) => {
    const selected = String(email || '').trim()
    if (!selected) return

    setAdditionalCcEmails((prev) => {
      const value = String(prev || '')
      const lastComma = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'))
      const base = lastComma >= 0 ? `${value.slice(0, lastComma + 1)} ` : ''
      const existing = parseAdditionalCcEmails(base)
      if (
        existing.some((item) => item.toLowerCase() === selected.toLowerCase())
      ) {
        return `${base.trim()} `.trimStart()
      }
      return `${base}${selected}, `
    })
    ccSearchRequestIdRef.current += 1
    setCcUserSuggestions([])
    setIsSearchingCcUsers(false)
  }

  const handleAdditionalCcBlur = () => {
    // Delay so suggestion clicks (onMouseDown) land first
    setTimeout(() => {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
    }, 180)
  }

  const fetchQuote = useCallback(async () => {
    if (!id) {
      toast.error('Sales quote ID is missing', { autoClose: 2000 })
      navigate('/salesQuotes')
      return
    }

    setIsLoading(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-sales-quote-by-id',
        id
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        const row = pickQuoteFromResponse(responseData, id)
        if (row) {
          setQuote(row)
        } else {
          toast.error('Sales quote not found', { autoClose: 2000 })
          navigate('/salesQuotes')
        }
      } else {
        toast.error(responseData?.message || 'Failed to fetch sales quote details', {
          autoClose: 2000
        })
        navigate('/salesQuotes')
      }
    } catch (error) {
      console.error('Fetch sales quote error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(
        errData?.message || 'An error occurred while fetching sales quote details',
        { autoClose: 2000 }
      )
      navigate('/salesQuotes')
    } finally {
      setIsLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    fetchQuote()
  }, [fetchQuote])

  useEffect(() => {
    const fetchUserEmail = async () => {
      const userId = localStorage.getItem('id')
      if (!userId) return
      try {
        const response = await axios.post(API_URL, {
          action: 'get-user',
          id: userId
        })
        const responseData = parseApiResponse(response.data)
        if (isApiSuccess(responseData) && responseData.user?.email) {
          setUserEmail(String(responseData.user.email).trim())
        }
      } catch (error) {
        console.error('Fetch user email error:', error)
      }
    }
    fetchUserEmail()
  }, [])

  const handleResendEmail = async (customer) => {
    const customerEmail = String(customer?.customerMailId || '').trim()
    const userId = localStorage.getItem('id')

    if (!id) {
      toast.error('Sales quote ID is missing', { autoClose: 2000 })
      return
    }
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }
    if (!customerEmail) {
      toast.error('Customer email is missing', { autoClose: 2000 })
      return
    }
    if (String(quote?.status).toLowerCase() !== 'active') {
      toast.error('Sales quote must be Active before resending email', { autoClose: 2000 })
      return
    }
    if (!hasSalesQuotePdf(quote)) {
      toast.error('PDF is not available for this sales quote', { autoClose: 2000 })
      return
    }

    setResendingFor(customerEmail)
    try {
      const ccEmails = buildCcEmails(userEmail, additionalCcEmails)
      const invalidCc = ccEmails.find((email) => !isValidEmailAddress(email))
      if (invalidCc) {
        toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 })
        return
      }
      if (ccEmails.length === 0) {
        toast.error('Could not get your email for CC. Add CC emails or check your profile.', {
          autoClose: 2000
        })
        return
      }

      const response = await axios.post(API_URL, {
        action: 'resend-sales-quote-email',
        id,
        customerMailId: customerEmail,
        userid: userId,
        ccEmails
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        const result = responseData?.data?.result
        const failCount = Number(responseData?.data?.failCount) || 0
        const sendOk = result?.success !== false && failCount === 0

        if (!sendOk) {
          toast.error(
            result?.message || responseData?.message || 'Failed to resend email',
            { autoClose: 2000 }
          )
          return
        }

        toast.success(
          responseData?.message || `Email resent to ${customerEmail}`,
          { autoClose: 2000 }
        )
        setQuote((prev) => applyResendEmailToQuote(prev, responseData, customerEmail))
      } else {
        toast.error(responseData?.message || 'Failed to resend email', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Resend sales quote email error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while resending email', {
        autoClose: 2000
      })
    } finally {
      setResendingFor('')
    }
  }

  const handleViewPdf = async () => {
    if (!hasSalesQuotePdf(quote)) {
      toast.error('PDF is not available for this sales quote', { autoClose: 2000 })
      return
    }

    setIsOpeningPdf(true)
    try {
      const downloadUrl = await resolveSqPdfDownloadUrl({
        pdfDownloadUrl: quote.pdfDownloadUrl,
        pdfLink: quote.pdfLink,
        quoteId: id
      })
      setQuote((prev) =>
        prev && prev.pdfDownloadUrl === downloadUrl
          ? prev
          : { ...prev, pdfDownloadUrl: downloadUrl }
      )
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Open sales quote PDF error:', error)
      toast.error(error.message || 'Failed to open PDF', { autoClose: 2000 })
    } finally {
      setIsOpeningPdf(false)
    }
  }

    const downloadOwnerAttachment = async (s3Key, fallbackUrl) => {
    if (!s3Key || downloadingS3Key) return

    setDownloadingS3Key(s3Key)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-sq-owner-attachment-download-presigned-url',
        s3Keys: [s3Key],
        expiresIn: 3600
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const files = responseData.files || responseData.data?.files || []
      const downloadUrl =
        files[0]?.downloadUrl ||
        files[0]?.url ||
        files[0]?.presignedUrl ||
        responseData.downloadUrl ||
        fallbackUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download attachment error:', error)
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('Failed to download attachment', { autoClose: 2000 })
      }
    } finally {
      setDownloadingS3Key('')
    }
  }

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View Sales Quote</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to="/salesQuotes">
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!quote) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>Sales quote not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const materials = Array.isArray(quote.materials) ? quote.materials : []
  const materialGrossTotals = getQuoteGrossTotals(quote, materials)
  const quoteTermsRows = getQuoteTermsRows(quote)
  const customers = getQuoteCustomers(quote)
  const quoteNo = quote.quoteNumber || '-'

  return (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">
                    <SectionTitle icon="fa-file-lines" title="Quote Details" />

                    <ViewField label="Quotation Number">{quoteNo}</ViewField>
                    <ViewField label="Quotation Date">{formatDate(quote.quoteDate)}</ViewField>
                    <ViewField label="Quotation Validity Date">
                      {formatDateTime(quote.quoteDeliveryTime)}
                    </ViewField>
                    <ViewField label="Currency">{quote.quoteCurrency || '-'}</ViewField>
                    <ViewField label="Quote Status">
                      <span className="fwt_600">{capitalizeStatus(quote.quoteStatus)}</span>
                    </ViewField>
                    <ViewField label="Status">
                      <span
                        className={`badge ${
                          String(quote.status).toLowerCase() === 'active'
                            ? 'badge-success'
                            : 'badge-secondary'
                        }`}
                        style={{ fontSize: '90%', fontWeight: 550 }}
                      >
                        {capitalizeStatus(quote.status)}
                      </span>
                    </ViewField>
                    <ViewField label="Quotation Summary">
                      <span style={{ whiteSpace: 'pre-wrap' }}>{quote.quoteSummary || '-'}</span>
                    </ViewField>

                    <SectionTitle icon="fa-list-check" title="Terms" />

                    {quoteTermsRows.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No terms</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                              <th style={{ fontSize: '12px', minWidth: '160px' }}>Quote Term</th>
                              <th style={{ fontSize: '12px', minWidth: '200px' }}>Quote Response</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quoteTermsRows.map((term, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td
                                  style={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {term.quoteTerm}
                                </td>
                                <td
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {term.quoteResponse}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-boxes-stacked" title="Materials" />

                    {materials.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No materials</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0" style={{ minWidth: '1100px' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px' }}>S.No</th>
                              <th style={{ fontSize: '12px' }}>Material Code</th>
                              <th style={{ fontSize: '12px' }}>Material Description</th>
                              <th style={{ fontSize: '12px' }}>PR Number</th>
                              <th style={{ fontSize: '12px' }}>HSN</th>
                              <th style={{ fontSize: '12px' }}>UOM</th>
                              <th style={{ fontSize: '12px' }}>Quantity</th>
                              <th style={{ fontSize: '12px' }}>Basic Price</th>
                              <th style={{ fontSize: '12px' }}>GST %</th>
                              <th style={{ fontSize: '12px' }}>GST Value</th>
                              <th style={{ fontSize: '12px' }}>Total Value</th>
                              <th style={{ fontSize: '12px' }}>Technical Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {materials.map((row, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{row.materialCode || '-'}</td>
                                <td
                                  style={{
                                    maxWidth: '240px',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {row.materialDescription || '-'}
                                </td>
                                <td>{row.prNumber || row.prNo || '-'}</td>
                                <td>{row.hsn || row.hsnCode || '-'}</td>
                                <td>{row.uom || '-'}</td>
                                <td>{row.quantity ?? '-'}</td>
                                <td>{formatMaterialMoney(row.basicPrice)}</td>
                                <td>
                                  {getMaterialGstPercent(row) != null
                                    ? `${getMaterialGstPercent(row)}%`
                                    : '-'}
                                </td>
                                <td>{getMaterialGstValueDisplay(row)}</td>
                                <td>{getMaterialTotalValueDisplay(row)}</td>
                                <td
                                  style={{
                                    maxWidth: '200px',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {row.technicalRemarks || '-'}
                                </td>
                              </tr>
                            ))}
                            <tr style={{ backgroundColor: '#f4f6f9' }}>
                              <td
                                colSpan={7}
                                className="text-right align-middle"
                                style={{ fontSize: '13px', fontWeight: 600 }}
                              >
                                Gross Total
                                {quote.quoteCurrency ? ` (${quote.quoteCurrency})` : ''}
                              </td>
                              <td
                                className="align-middle"
                                style={{ fontSize: '13px', fontWeight: 600 }}
                              >
                                {formatMaterialMoney(materialGrossTotals.grossTotalBasicPrice)}
                              </td>
                              <td />
                              <td
                                className="align-middle"
                                style={{ fontSize: '13px', fontWeight: 600 }}
                              >
                                {formatMaterialMoney(materialGrossTotals.grossGstValue)}
                              </td>
                              <td
                                className="align-middle"
                                style={{ fontSize: '13px', fontWeight: 600 }}
                              >
                                {formatMaterialMoney(materialGrossTotals.grossTotalValue)}
                              </td>
                              <td />
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-envelope" title="Customer Mail ID" />

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <div className="form-group mb-2">
                          <label>CC Emails (your email)</label>
                          <div className="txtdsply" style={{ fontSize: '13px' }}>
                            {userEmail || (
                              <span className="text-muted">
                                Profile email not available — add CC addresses below
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group mb-2">
                          <label>Additional CC Emails</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. manager@example.com"
                              value={additionalCcEmails}
                              onChange={handleAdditionalCcChange}
                              onBlur={handleAdditionalCcBlur}
                              disabled={Boolean(resendingFor)}
                              autoComplete="off"
                            />
                            {(isSearchingCcUsers || ccUserSuggestions.length > 0) && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  zIndex: 1060,
                                  backgroundColor: '#fff',
                                  border: '1px solid #d8e0ea',
                                  borderRadius: '8px',
                                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                                  maxHeight: '210px',
                                  overflowY: 'auto',
                                  marginTop: '2px'
                                }}
                              >
                                {isSearchingCcUsers ? (
                                  <div
                                    className="text-muted"
                                    style={{ padding: '10px 12px', fontSize: '13px' }}
                                  >
                                    <i className="fa-solid fa-spinner fa-spin mr-2" />
                                    Searching users & customer contacts...
                                  </div>
                                ) : (
                                  (() => {
                                    const existing = new Set(
                                      buildCcEmails(userEmail, additionalCcEmails).map(
                                        (item) => item.toLowerCase()
                                      )
                                    )
                                    const visible = ccUserSuggestions.filter(
                                      (item) =>
                                        !existing.has(
                                          String(item.email).trim().toLowerCase()
                                        )
                                    )
                                    if (visible.length === 0) {
                                      return (
                                        <div
                                          className="text-muted"
                                          style={{ padding: '10px 12px', fontSize: '13px' }}
                                        >
                                          No matching users or customer contacts found
                                        </div>
                                      )
                                    }
                                    return visible.map((item) => (
                                      <button
                                        key={item.email}
                                        type="button"
                                        className="dropdown-item"
                                        style={{
                                          display: 'block',
                                          width: '100%',
                                          textAlign: 'left',
                                          padding: '8px 12px',
                                          whiteSpace: 'normal',
                                          cursor: 'pointer'
                                        }}
                                        onMouseDown={(e) => {
                                          e.preventDefault()
                                          handleSelectCcUser(item.email)
                                        }}
                                      >
                                        <span
                                          className="d-block fwt_600"
                                          style={{ fontSize: '13px' }}
                                        >
                                          {item.name || item.email}
                                        </span>
                                        <small className="text-muted d-block">
                                          {item.email}
                                        </small>
                                        {item.sourceLabel && (
                                          <small className="text-muted">
                                            {item.sourceLabel}
                                          </small>
                                        )}
                                      </button>
                                    ))
                                  })()
                                )}
                              </div>
                            )}
                          </div>
                          <small className="form-text text-muted">
                            Comma-separated. Included when resending to customers.
                          </small>
                          {buildCcEmails(userEmail, additionalCcEmails).length > 0 && (
                            <small className="form-text text-muted d-block">
                              Will CC: {buildCcEmails(userEmail, additionalCcEmails).join(', ')}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>

                    {customers.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No customers</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px' }}>S.No</th>
                              <th style={{ fontSize: '12px' }}>Customer Name</th>
                              <th style={{ fontSize: '12px' }}>Email</th>
                              <th style={{ fontSize: '12px' }}>Phone Number</th>
                              <th style={{ fontSize: '12px', width: '100px' }}>Resend</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customers.map((row, index) => {
                              const customerEmail = String(row.customerMailId || '').trim()
                              const isResending = resendingFor === customerEmail
                              return (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td
                                    style={{
                                      maxWidth: '220px',
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {row.customerName || '-'}
                                  </td>
                                  <td>{row.customerMailId || '-'}</td>
                                  <td>{row.phoneNumber || row.phonenumber || '-'}</td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      title="Resend sales quote email to customer"
                                      onClick={() => handleResendEmail(row)}
                                      disabled={
                                        !customerEmail ||
                                        isResending ||
                                        String(quote.status).toLowerCase() !== 'active' ||
                                        !hasSalesQuotePdf(quote)
                                      }
                                    >
                                      {isResending ? (
                                        <i className="fa-solid fa-spinner fa-spin" />
                                      ) : (
                                        <i className="fa-solid fa-paper-plane" />
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                   {(hasSalesQuotePdf(quote) || ownerAttachments.length > 0) && (
                      <>
                        <SectionTitle icon="fa-file-pdf" title="Document" />
                        {hasSalesQuotePdf(quote) ? (
                          <ViewField label="PDF Link">
                            <button
                              type="button"
                              className="btn btn-link p-0 align-baseline"
                              onClick={handleViewPdf}
                              disabled={isOpeningPdf}
                            >
                              {isOpeningPdf ? (
                                <>
                                  <i className="fa-solid fa-spinner fa-spin mr-1" />
                                  Opening...
                                </>
                              ) : (
                                'View PDF'
                              )}
                            </button>
                          </ViewField>
                        ) : null}

                        {ownerAttachments.length > 0 ? (
                          <ViewField label="Owner Attachments">
                            <AttachmentTileGrid
                              attachments={ownerAttachments}
                              downloadingS3Key={downloadingS3Key}
                              onDownload={downloadOwnerAttachment}
                              disabled={Boolean(downloadingS3Key)}
                            />
                          </ViewField>
                        ) : null}
                      </>
                    )}

                    <SectionTitle icon="fa-clock" title="Audit" />

                    <ViewField label="Created By">{displayUser(quote.createdBy)}</ViewField>
                    <ViewField label="Updated By">{displayUser(quote.updatedBy)}</ViewField>
                    <ViewField label="Created At">{formatDateTime(quote.createdAt)}</ViewField>
                    <ViewField label="Updated At">{formatDateTime(quote.updatedAt)}</ViewField>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SalesQuotesView
